import {Buffer} from 'buffer';

import {filter, map, xor, isEmpty, sortBy, reduce, isEqual, transform, cloneDeep} from 'lodash';
import pretty from 'pretty';
import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import utf8 from 'utf8';

import {userSettingsApi} from 'api';
import {
    Button, DataWrapper, ConfirmModal, Icon, Dropdown, DropdownItem,
    FileInput, ActionButton, PredefinedNotifierType, notifier, AssignmentSelectors,
    Accordion, AccordionTitle, AccordionDetails, UnsavedChangesLeavePrompt, AccordionsExpandedMap
} from 'components';
import {DynamicComponentNames, ClientDynamicComponentsConfig} from 'constants/client';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {DynamicContentComponent} from 'types';
import {apiUtils, userAuthUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

import {ComponentDescription} from './ComponentDescription';
import {CreateComponentModal} from './CreateComponentModal';
import {WhiteLabelEditor} from './WhiteLabelEditor';
import {WhiteLabelPreviewModal, WhiteLabelPreviewModalProps} from './WhiteLabelPreviewModal';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL;

export const WhiteLabel = () => {
    const {t} = useTranslation();
    const {settings: {featuresAvailability}} = useContext(UserContext);

    const [importRef, setImportRef] = useState(useRef<HTMLInputElement>(null));
    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState<{
        open: boolean,
        name: DynamicComponentNames
    }>({
        open: false,
        name: null
    });
    const [createComponentModalProps, setCreateComponentModalProps] = useState({
        open: false,
        options: []
    });

    const [whiteLabelPreviewModalProps, setWhiteLabelPreviewModalProps] = useState<WhiteLabelPreviewModalProps>({
        open: false,
        component: {
            template: null,
            styles: null,
            name: null
        },
        onTemplateUpdate: null,
        onStylesUpdate: null,
        onResetTemplate: null,
        onResetStyles: null,
        onClose: null,
        clientSettings: null
    });

    const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
    const [components, setComponents] = useState<DynamicContentComponent[]>([]);
    const [originComponents, setOriginComponents] = useState<DynamicContentComponent[]>([]);
    const [accordionsExpandedMap, setAccordionsExpandedMap] = useState<AccordionsExpandedMap>({});

    const downloadFileDisabled = useMemo(() => userAuthUtils.isDownloadFileDisabledAuthSource(), []);

    const fetchDataFn = useCallback(() => userSettingsApi.getClientWhiteLabel(selectedGroupId), [selectedGroupId]);
    const {
        send: fetchData,
        loading: fetchInProgress,
        data: whiteLabel
    } = useApiData(fetchDataFn, {notificationType: PredefinedNotifierType.FetchError});

    const {
        send: fetchClientSettings,
        data: clientSettings
    } = useApiData(userSettingsApi.getClientSettings, {notificationType: PredefinedNotifierType.FetchError});

    const handleExport = useCallback(() => {
        const exportData = map(components, ({name, template, styles}) => ({name, template, styles}));
        const base64 = Buffer.from(JSON.stringify(exportData)).toString('base64');
        apiUtils.saveFileFromBase64(`${t(T_KEYS.EXPORT_FILENAME)}.json`, base64);
    }, [t, components]);

    const availableComponentsToCreate = useMemo(() => {
        const componentNames = xor(
            (Object.values(DynamicComponentNames)).sort(),
            map(components, ({name}) => name)
        );

        return filter(componentNames, (name) => featuresAvailability.avatar || name !== DynamicComponentNames.Avatar);
    }, [components, featuresAvailability]);

    const saveChanges = useCallback(async () => {
        const saveResult = await userSettingsApi.saveClientWhiteLabel({
            groupId: selectedGroupId || null,
            components
        });

        await fetchData();
        return saveResult;
    }, [components, selectedGroupId, fetchData]);

    const isWarningChecker = useCallback((saveResult) => !isEmpty(saveResult?.sanitizedComponents), []);

    const renderWarningDetails = useCallback((sanitizedComponentNames: string[]) => (
        <Fragment>
            <div>{t(T_KEYS.MESSAGES.SANITIZE_WARNING_DETAILS)}</div>
            <div className='tw-font-bold'>{sanitizedComponentNames.join(', ')}</div>
        </Fragment>
    ), [t]);

    const buildNotifierWarningProps = useCallback((saveResult) => {
        const title = t(T_KEYS.MESSAGES.SANITIZE_WARNING_TITLE);
        const sanitizedComponents = map(saveResult?.sanitizedComponents, (c) => {
            const translationKey = buildTranslationKey(c.name);
            return t(T_KEYS.COMPONENTS?.[translationKey]?.NAME);
        });

        const details = renderWarningDetails(sanitizedComponents);
        return {title, details};
    }, [t, renderWarningDetails]);

    const {
        send: handleSave,
        loading: saveInProgress
    } = useApiData(saveChanges, {
        notificationType: PredefinedNotifierType.Save,
        buildNotifierWarningProps,
        isWarningChecker
    });

    const getDefaultTemplate = useCallback((name: string) => {
        const template = ClientDynamicComponentsConfig[name].defaultTemplate || '';
        return pretty(template);
    }, []);

    const hasNoChanges = useMemo(() => {
        const bothEmpty = isEmpty(components) && isEmpty(originComponents);
        const noChanges = isEqual(components, originComponents);
        return bothEmpty || noChanges;
    }, [components, originComponents]);

    const handleComponentCreate = useCallback((confirm: boolean, name: DynamicComponentNames) => {
        if (confirm) {
            const component = {
                name,
                key: buildTranslationKey(name),
                template: getDefaultTemplate(name),
                styles: ''
            } as DynamicContentComponent;

            setAccordionsExpandedMap((prev) => ({...prev, [name]: true}));
            setComponents((prev) => [...prev, component]);
        }
        setCreateComponentModalProps({
            open: false,
            options: []
        });
    }, [setCreateComponentModalProps, getDefaultTemplate]);

    const handleTemplateUpdate = useCallback((name: string, value: string) => {
        setComponents((prev) => map(prev, (comp) => {
            if (comp.name === name) {
                comp.template = value;
            }
            return comp;
        }));
    }, [setComponents]);

    const handleStylesUpdate = useCallback((name: string, value: string) => {
        setComponents((prev) => map(prev, (comp) => {
            if (comp.name === name) {
                comp.styles = value;
            }
            return comp;
        }));
    }, [setComponents]);

    const resetTemplate = useCallback((name: string) => {
        handleTemplateUpdate(name, getDefaultTemplate(name));
    }, [handleTemplateUpdate, getDefaultTemplate]);

    const resetStyles = useCallback((name: string) => {
        handleStylesUpdate(name, '');
    }, [handleStylesUpdate]);

    const handleCloseConfirmDeleteModal = useCallback((confirm: boolean) => {
        if (confirm) {
            setComponents((prev) => filter(prev, (comp) => comp.name !== confirmDeleteModalProps.name));
        }

        setConfirmDeleteModalProps({
            open: false,
            name: null
        });
    }, [setComponents, setConfirmDeleteModalProps, confirmDeleteModalProps.name]);

    const handleCloseWhiteLabelPreviewModal = useCallback(() => {
        setWhiteLabelPreviewModalProps((prev) => ({...prev, open: false}));
    }, [setWhiteLabelPreviewModalProps]);

    const handleExpandedChange = useCallback((name: string, expanded: boolean) => {
        setAccordionsExpandedMap((prev) => ({...prev, [name]: expanded}));
    }, [setAccordionsExpandedMap]);

    useEffect(() => {
        setAccordionsExpandedMap((prev) => transform(components, (result, component) => {
            result[component.name] = !!prev[component.name];
        }, {}));
    }, [components]);

    const openImportFileInput = useCallback(() => {
        importRef?.current?.click?.();
    }, [importRef]);

    const handleImportFile = useCallback((buffer: ArrayBuffer) => {
        try {
            const json: DynamicContentComponent[] =
                JSON.parse(utf8.decode(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer)))));
            const componentNames = Object.values(DynamicComponentNames);
            const importedComponents = reduce(json, (acc, component) => {
                if (componentNames.includes(component?.name)) {
                    acc.push({name: component.name, template: pretty(component.template), styles: component.styles});
                }
                return acc;
            }, []);

            setComponents(importedComponents);
        } catch {
            notifier.error({title: t(T_KEYS.IMPORT_FAILED)});
        }
    }, [t, setComponents]);

    useEffect(() => {
        const prettyContentComponents = map(whiteLabel?.components, (comp) => ({
            ...comp,
            template: pretty(comp.template),
        }));
        setComponents(prettyContentComponents);
        setOriginComponents(cloneDeep(prettyContentComponents));
    }, [whiteLabel]);

    useEffect(() => {
        fetchData();
        fetchClientSettings(selectedGroupId);
    }, [fetchData, fetchClientSettings, selectedGroupId]);

    const preview = useCallback((component) => {
        setWhiteLabelPreviewModalProps((prev) => ({
            ...prev,
            component,
            open: true
        }));
    }, [setWhiteLabelPreviewModalProps]);

    return (
        <Fragment>
            <div className='tw-text-default-dimmed'>
                <div className='tw-mb-2'>{t(T_KEYS.DESCRIPTION.P1)}</div>
                <div>{t(T_KEYS.DESCRIPTION.P2)}</div>
            </div>
            <div className='tw-flex tw-gap-2 tw-my-4 tw-items-end -md:tw-flex-col md:tw-justify-between'>
                <AssignmentSelectors
                    selectedGroupId={selectedGroupId}
                    onChange={setSelectedGroupId}
                >
                    {({renderGroup}) => (
                        <div className='tw-w-full md:tw-w-64 tw-flex-shrink-1'>{renderGroup()}</div>
                    )}
                </AssignmentSelectors>
                <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-2 -md:tw-my-4'>
                    <ActionButton
                        action='create'
                        className='tw-ml-auto'
                        disabled={fetchInProgress || isEmpty(availableComponentsToCreate)}
                        onClick={() => setCreateComponentModalProps({open: true, options: availableComponentsToCreate})}
                    >
                        {t(T_KEYS.ADD_BTN)}
                    </ActionButton>
                    <div className='tw-flex tw-items-center -sm:tw-ml-auto'>
                        <Button
                            width='small'
                            color='primary'
                            className='tw-mr-2'
                            loading={saveInProgress}
                            disabled={fetchInProgress || saveInProgress || hasNoChanges}
                            onClick={handleSave}
                        >
                            {t(T_KEYS.SAVE_BTN)}
                        </Button>
                        <Dropdown triggerNode={
                            <Fragment>
                                <Button
                                    width='small'
                                    className='-md:tw-hidden'
                                    disabled={fetchInProgress || saveInProgress}
                                    icon='more-vertical'
                                >
                                    {t(T_KEYS.ACTIONS_BTN)}
                                </Button>
                                <div className='md:tw-hidden'>
                                    <Icon
                                        disabled={fetchInProgress || saveInProgress}
                                        name='more-vertical'
                                        button
                                    />
                                </div>
                            </Fragment>
                        }
                        >
                            <DropdownItem disabled={isEmpty(components) || downloadFileDisabled} onClick={handleExport}>
                                {t(T_KEYS.EXPORT_BTN)}
                            </DropdownItem>
                            <DropdownItem onClick={openImportFileInput}>{t(T_KEYS.IMPORT_BTN)}</DropdownItem>
                        </Dropdown>
                    </div>
                </div>
            </div>

            <DataWrapper
                isLoading={fetchInProgress || saveInProgress}
                noData={isEmpty(components)}
                noDataClassName='tw-h-32'
            >
                {map(sortBy(components, 'name'), (component, index) => {
                    const {name, template, styles} = component;
                    const translationKey = buildTranslationKey(name);
                    return (
                        <Accordion
                            key={index}
                            className='tw-my-2'
                            expanded={!!accordionsExpandedMap[name]}
                            onChange={(expanded) => handleExpandedChange(name, expanded)}
                        >
                            <AccordionTitle>
                                <div className='tw-flex tw-justify-between'>
                                    <div className='tw-font-bold tw-text-lg'>{t(T_KEYS.COMPONENTS?.[translationKey]?.NAME)}</div>
                                    <div>
                                        <Icon
                                            name='delete'
                                            button
                                            color='alert'
                                            stopPropagation
                                            onClick={() => setConfirmDeleteModalProps({open: true, name})}
                                        />
                                    </div>
                                </div>
                            </AccordionTitle>
                            <AccordionDetails>
                                <ComponentDescription name={name} />
                                <div className='md:tw-grid md:tw-grid-cols-2 md:tw-gap-8 tw-mb-2'>
                                    <WhiteLabelEditor
                                        language='html'
                                        placeholder={t(T_KEYS.TEMPLATE_PLACEHOLDER)}
                                        value={template}
                                        onUpdate={(value) => handleTemplateUpdate(name, value)}
                                        onReset={() => resetTemplate(name)}
                                        onPreview={() => preview(component)}
                                    />

                                    <WhiteLabelEditor
                                        language='css'
                                        placeholder={t(T_KEYS.STYLES_PLACEHOLDER)}
                                        value={styles}
                                        onUpdate={(value) => handleStylesUpdate(name, value)}
                                        onReset={() => resetStyles(name)}
                                        onPreview={() => preview(component)}
                                    />
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </DataWrapper>

            <FileInput accept='.json' setInputRef={setImportRef} onUpload={handleImportFile} />

            <CreateComponentModal {...createComponentModalProps} onClose={handleComponentCreate} />

            <ConfirmModal
                open={confirmDeleteModalProps.open}
                title={t(T_KEYS.CONFIRM_REMOVE)}
                onClose={handleCloseConfirmDeleteModal}
            />

            <WhiteLabelPreviewModal
                {...whiteLabelPreviewModalProps}
                clientSettings={clientSettings}
                onTemplateUpdate={handleTemplateUpdate}
                onStylesUpdate={handleStylesUpdate}
                onResetTemplate={resetTemplate}
                onResetStyles={resetStyles}
                onClose={handleCloseWhiteLabelPreviewModal}
            />

            <UnsavedChangesLeavePrompt values={components} initialValues={whiteLabel?.components} />
        </Fragment>
    );
};